import React from "react";
import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Checkbox,
  Link,
  Heading,
  Image,
} from "@chakra-ui/react";

interface RegistrationFormProps {
  formData: {
    firstname: string;
    lastname: string;
    jobTitle?: string;
    company?: string;
    mobilePhone: string;
    email: string;
  };
  isLoading: boolean;
  privacyAccepted: boolean;
  onPrivacyChange: (checked: boolean) => void;
  onOpenPrivacy: () => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  formData,
  isLoading,
  privacyAccepted,
  onPrivacyChange,
  onOpenPrivacy,
  onChange,
  onSubmit,
}) => {
  const inputStyles = {
    bg: "rgba(255, 255, 255, 0.15)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    color: "white",
    _placeholder: { color: "whiteAlpha.400" },
    _hover: {
      bg: "rgba(255, 255, 255, 0.12)",
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    _focus: {
      bg: "rgba(255, 255, 255, 0.12)",
      borderColor: "pink.200",
      boxShadow: "none",
    },
    fontSize: "md",
    h: "45px",
    sx: {
      "&:autofill, &:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus":
        {
          border: "1px solid rgba(255, 255, 255, 0.1)",
          WebkitTextFillColor: "white",
          transition: "background-color 5000s ease-in-out 0s",
          backgroundColor: "rgba(255, 255, 255, 0.08) !important",
        },
      "&:-internal-autofill-selected, &:-internal-autofill-previewed": {
        border: "1px solid rgba(255, 255, 255, 0.1) !important",
        WebkitTextFillColor: "white !important",
        backgroundColor: "rgba(255, 255, 255, 0.08) !important",
      },
    },
  };

  return (
    <Box
      as="form"
      onSubmit={onSubmit}
      w="100%"
      p={8}
      mx={3.5}
      borderRadius="2xl"
      bg="rgba(160, 255, 255, 0.15)"
      backdropFilter="blur(40px)"
      border="1px solid rgba(255, 255, 255, 0.1)"
      boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.3)"
      transition="all 0.3s ease-in-out"
    >
      <VStack spacing={5}>
        <Image
          src="/cee-logo.png"
          alt="CEE Logo"
          width="72px"
          height="auto"
          mb={3}
          mt={3}
          loading="eager"
          draggable={false}
        />
        <Heading
          color="white"
          textAlign="center"
          fontSize="3xl"
          fontWeight="bold"
          textShadow="0 2px 4px rgba(0,0,0,0.3)"
        >
          CEE WHAT'S NEXT
        </Heading>
        <Box
          color="white"
          fontSize="md"
          mb={4}
          textAlign="center"
          opacity={0.9}
        >
          Join us on March 28, 2025, to discover the future of the media
          industry as Ceemeagain reveals upcoming changes, major
          transformations, and new strategies.
          <br />
          The event starts at 3:00 PM at SCBX NEXT TECH (registration begins at
          2:30 PM).
        </Box>
        <FormControl isRequired>
          <FormLabel fontWeight="normal" color="white">
            First Name
          </FormLabel>
          <Input
            name="firstname"
            value={formData.firstname}
            onChange={onChange}
            placeholder="Enter your first name"
            {...inputStyles}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="normal" color="white">
            Last Name
          </FormLabel>
          <Input
            name="lastname"
            value={formData.lastname}
            onChange={onChange}
            placeholder="Enter your last name"
            {...inputStyles}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="normal" color="white">
            Email
          </FormLabel>
          <Input
            name="email"
            value={formData.email}
            onChange={onChange}
            placeholder="Enter your email address"
            type="email"
            {...inputStyles}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontWeight="normal" color="white">
            Mobile Phone
          </FormLabel>
          <Input
            name="mobilePhone"
            value={formData.mobilePhone}
            onChange={onChange}
            placeholder="e.g. 081-234-5678"
            type="tel"
            maxLength={12}
            {...inputStyles}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="normal" color="white">
            Company
          </FormLabel>
          <Input
            name="company"
            value={formData.company}
            onChange={onChange}
            placeholder="Enter your company name"
            {...inputStyles}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontWeight="normal" color="white">
            Job Title
          </FormLabel>
          <Input
            name="jobTitle"
            value={formData.jobTitle}
            onChange={onChange}
            placeholder="Enter your job title"
            {...inputStyles}
          />
        </FormControl>

        <Box w="100%" textAlign="left">
          <Checkbox
            isChecked={privacyAccepted}
            onChange={(e) => onPrivacyChange(e.target.checked)}
            colorScheme="pink"
            color="white"
            sx={{
              ".chakra-checkbox__control": {
                borderColor: "whiteAlpha.300",
                bg: "transparent",
                _checked: {
                  bg: "pink.400",
                  borderColor: "pink.400",
                },
              },
            }}
          >
            I agree to the{" "}
            <Link
              color="pink.300"
              onClick={onOpenPrivacy}
              _hover={{ color: "pink.600" }}
              fontWeight="normal"
            >
              Privacy Policy
            </Link>
          </Checkbox>
        </Box>

        <Button
          type="submit"
          width="100%"
          isLoading={isLoading}
          isDisabled={!privacyAccepted}
          bg="pink.500"
          color="white"
          fontSize="md"
          h="45px"
          _hover={{
            bg: "pink.400",
          }}
          _active={{
            bg: "pink.600",
          }}
        >
          Register
        </Button>
      </VStack>
    </Box>
  );
};
